import { AgendaApi } from 'services';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { find } from 'lodash';
import { toastMessages } from 'config/constants';
import { toCamelCase } from 'utils';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';

const BookingAgendaContext = createContext(undefined);
const BookingAgendaFunctionsContext = createContext(undefined);

function getEventCapacityColor(capacity) {
  if (capacity === 0) return '#646464';
  else if (capacity < 50) return '#16B84E';
  else if (capacity >= 50 && capacity < 75) return '#FDA50F';
  else if (capacity >= 75 && capacity < 100) return '#FDA50F';
  else return '#DC143C';
}

function BookingAgendaProvider({ children, monitorId, onLoaded }) {
  const [appointments, setAppointments] = useState([]);
  const [agendaConfig, setAgendaConfig] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);
  const [isLoadingMonitorAgenda, setIsLoadingMonitorAgenda] = useState(false);
  const selectedAppointment = useMemo(
    () => find(appointments, { id: selectedAppointmentId })?.data,
    [selectedAppointmentId, appointments],
  );

  useEffect(() => {
    onLoaded?.(isLoadingAgenda);
  }, [isLoadingAgenda, onLoaded]);

  async function loadAgendaConfiguration(monitorId) {
    let agendaConfig = null;
    try {
      ({ agenda_config: agendaConfig } = await AgendaApi.getAgendaConfigByEmployee(monitorId));

      setAgendaConfig(agendaConfig);
    } catch (_) {
      toast.error(toastMessages.error.FETCH_AGENDA_CONFIGURATION);
    }

    if (!agendaConfig) {
      setAppointments([]);
      setIsLoadingAgenda(false);
      setIsLoadingMonitorAgenda(false);
    }
    return agendaConfig;
  }

  async function loadMonitorAgenda(monitorId, agendaConfig) {
    setIsLoadingAgenda(true);
    try {
      const { activities } = await AgendaApi.getAgendaByDates(
        monitorId,
        agendaConfig.start_date,
        agendaConfig.end_date,
      );
      const existingAppointments = activities.map((activity, index) => {
        const startDate = new Date(activity.start_date);
        const endDate = new Date(activity.end_date);

        const capacity = Math.floor((activity.participantsCount / activity.service.clients_number) * 100);

        return {
          id: 'ap-' + index,
          title: activity.service.name,
          data: {
            id: 'ap-' + index,
            capacity,
            clients: activity.clients.map(toCamelCase),
            service: toCamelCase(activity.service),
            startDate,
            endDate,
          },
          activityStatus: '' + activity.clients.length + '/' + activity.service.clients_number,
          start: startDate,
          end: endDate,
          minAge: activity.min_age,
          maxAge: activity.max_age,
          textColor: '#000000',
          backgroundColor: getEventCapacityColor(capacity),
        };
      });
      setAppointments(existingAppointments);
    } catch (_) {
      toast.error(toastMessages.error.FETCH_AGENDA);
    }
    setIsLoadingAgenda(false);
    setIsLoadingMonitorAgenda(false);
  }

  const chooseAppointment = useCallback((appointment) => {
    setSelectedAppointmentId(appointment?.id);
  }, []);

  const createBooking = useCallback(
    async (body) => {
      try {
        const result = await AgendaApi.createBookingForEmployee(monitorId, body);
        const { booking, client, status } = result;

        if (status !== 'OK') throw status;

        setClients((prev) => [
          ...prev,
          {
            id: client.id,
            firstName: client.first_name,
            lastName: client.last_name,
            phone: client.phone,
            email: client.email,
            gender: client.gender,
            price: booking.price,
            paymentMethod: booking.payment_method,
            quantity: booking.quantity,
            bookingId: booking.id,
          },
        ]);

        loadMonitorAgenda(monitorId, agendaConfig);
        toast.success(toastMessages.success.CREATE_BOOKING);

        return { success: true, result };
      } catch (error) {
        toast.error(toastMessages.error.CREATE_BOOKING);
        return { success: false, result: {} };
      }
    },
    [monitorId, agendaConfig],
  );

  const updateBooking = useCallback(
    async (bookingId, { paymentAmount, paymentMethod }) => {
      const body = { payment_amount: paymentAmount, payment_method: paymentMethod };
      try {
        const response = await AgendaApi.updateBookingForEmployee(bookingId, monitorId, body);

        if (response.status === 'OK') {
          setAppointments((prev) => {
            let newAppointments = [...prev];
            const index = newAppointments.findIndex(
              ({ data }) => data.clients.find((c) => c.bookingId === bookingId) !== undefined,
            );
            const clientIndex = newAppointments[index].data.clients.findIndex((c) => c.bookingId === bookingId);
            newAppointments[index].data.clients[clientIndex].price = paymentAmount;
            newAppointments[index].data.clients[clientIndex].paymentMethod = paymentMethod;
            return newAppointments;
          });
        }

        toast.success(toastMessages.success.UPDATE_BOOKING);
        return response.status === 'OK';
      } catch (error) {
        toast.error(toastMessages.error.UPDATE_BOOKING);
        return false;
      }
    },
    [monitorId],
  );

  const removeBooking = useCallback(
    async (bookingId) => {
      setIsDeleting(true);
      try {
        const { status } = await AgendaApi.deleteBookingForEmployee(bookingId, monitorId);
        if (status === 'OK') {
          setClients((prev) => prev.filter((sc) => sc.id !== bookingId));
          await loadMonitorAgenda(monitorId, agendaConfig);
        }
        toast.success(toastMessages.success.DELETE_BOOKING);
      } catch (_) {
        toast.error(toastMessages.error.DELETE_BOOKING);
      }
      setIsDeleting(false);
    },
    [monitorId, agendaConfig],
  );

  useEffect(() => {
    setAppointments([]);
    if (!monitorId) return;
    setIsLoadingMonitorAgenda(true);
    setIsLoadingAgenda(true);
    loadAgendaConfiguration(monitorId).then((agendaConfig) => {
      if (agendaConfig) loadMonitorAgenda(monitorId, agendaConfig);
    });
  }, [monitorId]);

  useEffect(() => {
    setClients(selectedAppointment?.clients || []);
  }, [selectedAppointment?.clients]);

  const values = useMemo(
    () => ({
      appointments,
      clients,
      isDeleting,
      isLoadingAgenda,
      isLoadingMonitorAgenda,
      selectedAppointment,
    }),
    [appointments, clients, isDeleting, isLoadingAgenda, isLoadingMonitorAgenda, selectedAppointment],
  );

  const functions = useMemo(
    () => ({
      createBooking,
      chooseAppointment,
      updateBooking,
      removeBooking,
    }),
    [createBooking, chooseAppointment, updateBooking, removeBooking],
  );

  return (
    <BookingAgendaContext.Provider value={values}>
      <BookingAgendaFunctionsContext.Provider value={functions}>{children}</BookingAgendaFunctionsContext.Provider>
    </BookingAgendaContext.Provider>
  );
}

/**
 *
 * @returns {{
 *  appointments: Array<Object>,
 *  clients: Array<Object>,
 *  isDeleting: boolean,
 *  isLoadingAgenda: boolean,
 *  isLoadingMonitorAgenda: boolean,
 *  selectedAppointment: Object | null,
 * }}
 */
function useBookingAgenda() {
  return useGenericContext(
    BookingAgendaContext,
    'useBookingAgenda cannot be used outside of a BookingAgendaProvider component.',
  );
}

/**
 *
 * @returns {{
 *  createBooking: (values) => Promise<any>
 *  chooseAppointment: (appointment) => void
 *  updateBooking (bookingId, values) => Promise<any>
 *  removeBooking: (bookingId) => Promise<any>
 * }}
 */
function useBookingAgendaFunctions() {
  return useGenericContext(
    BookingAgendaFunctionsContext,
    'useBookingAgendaFunctions cannot be used outside of a BookingAgendaProvider component.',
  );
}

export { BookingAgendaProvider, useBookingAgenda, useBookingAgendaFunctions };
